import type { lastReported } from "@/config";
import { atom } from "jotai";
import type { MapRef } from "react-map-gl";

import { type AreaType, type MarkerType, initArea } from "../types";
import { getZoom } from "../utils";

export type ViewStateType = {
  latitude: number;
  longitude: number;
  zoom: number;
};

export type AreaLevelType = "country" | "county" | "city" | "borough";

export const viewStateAtom = atom<ViewStateType>({
  latitude: 54.052001,
  longitude: -5.331378,
  zoom: 5,
});

export const selectedTimeAtom = atom<keyof typeof lastReported>(6);

export const isMenuOpenAtom = atom(false);

export const transitioningAtom = atom(false);

export const showHeatmapAtom = atom(true);

export const showSecondGeocoderAtom = atom(false);

export type AreaCoordinates = {
  lat: number | null;
  long: number | null;
};

export const firstAreaCoordinatesAtom = atom<AreaCoordinates>({
  lat: null,
  long: null,
});

export const secondAreaCoordinatesAtom = atom<AreaCoordinates>({
  lat: null,
  long: null,
});

export const geolocateActiveAtom = atom(true);

export const showModalAtom = atom(false);

export const areaLevelAtom = atom<AreaLevelType>("country");

export const firstAreaAtom = atom<AreaType | null>(initArea());

export const secondAreaAtom = atom<AreaType | null>(null);

export const markersAtom = atom<MarkerType[]>([]);

export const userLocationMarkerAtom = atom<{
  latitude: number;
  longitude: number;
  counts: {
    yearCounts: {
      [key: number]: number;
    };
    lastReportedMonthCount: number;
  };
} | null>(null);

export const addMarkerSelector = atom(null, (get, set, marker) => {
  const newMarker = marker as MarkerType;
  const markers = get(markersAtom);
  const newMarkers = [...markers];
  const firstArea = get(firstAreaAtom);
  const isFirstArea = newMarker.location === firstArea?.name;

  if (isFirstArea) {
    newMarkers[0] = newMarker;
  } else {
    newMarkers[1] = newMarker;
  }

  set(markersAtom, newMarkers);
});

export const setSelectedAreaSelector = atom(null, (get, set, selectedArea) => {
  const firstArea = get(firstAreaAtom);
  const secondArea = get(secondAreaAtom);
  set(showHeatmapAtom, true);

  const area = selectedArea as AreaType;

  if (area.removeAll) {
    set(markersAtom, []);
    set(firstAreaAtom, area);
    set(secondAreaAtom, null);
  } else if (!firstArea) {
    set(firstAreaAtom, area);
  } else if (firstArea.name === area.name) {
    set(firstAreaAtom, null);
  } else if (secondArea?.name === area.name) {
    set(secondAreaAtom, null);
  } else set(secondAreaAtom, area);
});

export const addAreaSelector = atom(null, (get, set, selectedArea) => {
  const firstArea = get(firstAreaAtom);
  const secondArea = get(secondAreaAtom);
  set(showHeatmapAtom, true);

  const area = selectedArea as AreaType;

  if (area.removeAll) {
    set(markersAtom, []);
    set(firstAreaAtom, area);
    set(secondAreaAtom, null);
  } else if (!firstArea) {
    set(firstAreaAtom, area);
  } else if (firstArea.name === area.name) {
    set(firstAreaAtom, null);
  } else if (secondArea?.name === area.name) {
    set(secondAreaAtom, null);
  } else set(secondAreaAtom, area);
});

export const setAreaLevelSelector = atom(null, (get, set) => {
  const { zoom } = get(viewStateAtom);

  switch (true) {
    case zoom <= getZoom("country", true):
      set(areaLevelAtom, "country");
      break;
    case zoom > getZoom("country", true) && zoom < getZoom("district", true):
      set(areaLevelAtom, "county");
      break;
    case zoom >= getZoom("district", true) && zoom < getZoom("city", true):
      set(areaLevelAtom, "city");
      break;
    case zoom >= getZoom("city", true):
      set(areaLevelAtom, "borough");
      break;
    default:
      break;
  }
});

export const mapRefAtom = atom<MapRef | null>(null);

export const countryIdAtom = atom<number | null>(null);
